import { useState, useEffect } from "react";
import { useSelector } from "react-redux"; // Added react-redux import
import { getAccountStatements } from "../../../AsianExchangeSrc/api";
import Loader from "../../../AsianExchangeSrc/utils/Loader";
import { getUserId } from "../../../AsianExchangeSrc/utils/helper/commonSelectors";
import CommonTable from "../../components/TableComponent/TableComponent";

// Selector function (assuming it's defined in your redux store)

const AccountStatement = () => {
  const [dataSource, setDataSource] = useState("");
  const [sport, setSport] = useState("Cricket");
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split("T")[0];
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entriesPerPage, setEntriesPerPage] = useState("10");
  const loggedInUserId = useSelector(getUserId());

  // Handle date range changes based on data source
  useEffect(() => {
    const today = new Date();
    const formatDate = (date) => date.toISOString().split("T")[0];

    if (dataSource === "Live Data") {
      setStartDate(formatDate(today));
      setEndDate(formatDate(today));
    } else if (dataSource === "Backup Data") {
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      setStartDate(formatDate(threeMonthsAgo));
      setEndDate(formatDate(today));
    } else if (dataSource === "Old Data") {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(today.getFullYear() - 1);
      setStartDate(formatDate(oneYearAgo));
      setEndDate(formatDate(today));
    }
  }, [dataSource]);

  // Fetch account statements
  const getAccountSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAccountStatements({
        filterby: "account",
        startDate,
        endDate,
        sport,
      });
      setTransactions(res?.data?.transactions || []);
    } catch (error) {
      setError("Failed to fetch account statements. Please try again.");
      console.error("Error fetching account statements:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch and when filters change
  useEffect(() => {
    getAccountSummary();
  }, [startDate, endDate, sport]);

  const handleGetCommission = () => {
    getAccountSummary();
  };

  const processedTransactions = transactions.map((transaction) => {
    let deposit = "--";
    let withdraw = "--";
    let closingBalance = "--";
    let fromTo = "-";

    if (transaction.deposit > 0) {
      if (loggedInUserId === transaction?.userId?._id) {
        withdraw = Number(transaction.deposit).toFixed(2);
        closingBalance = transaction.userBalance?.toFixed(2) ?? "--";
        fromTo = `${transaction.userId?.userName || "User Deleted"} → ${
          transaction.toUserId?.userName || "User Deleted"
        }`;
      } else {
        deposit = Number(transaction.deposit).toFixed(2);
        closingBalance = transaction.toUserBalance?.toFixed(2) ?? "--";
        fromTo = `${transaction.userId?.userName || "User Deleted"} → ${
          transaction.toUserId?.userName || "User Deleted"
        }`;
      }
    } else if (transaction.withdraw > 0) {
      if (loggedInUserId === transaction?.userId?._id) {
        withdraw = Number(transaction.withdraw).toFixed(2);
        closingBalance = transaction.userBalance?.toFixed(2) ?? "--";
        fromTo = `${transaction.userId?.userName || "User Deleted"} → ${
          transaction.toUserId?.userName || "User Deleted"
        }`;
      } else {
        deposit = Number(transaction.withdraw).toFixed(2);
        closingBalance = transaction.toUserBalance?.toFixed(2) ?? "--";
        fromTo = `${transaction.userId?.userName || "User Deleted"} → ${
          transaction.toUserId?.userName || "User Deleted"
        }`;
      }
    }

    return {
      id: transaction._id,
      dateTime: transaction?.createdAt || "--",
      deposit: deposit,
      withdraw: withdraw,
      balance: closingBalance,
      remark: transaction.remark || "--",
      fromTo: fromTo,
    };
  });

  const columns = [
    { key: "dateTime", header: "Date/Time", mobileLabel: "Date/Time" },
    {
      key: "deposit",
      header: "Deposit",
      mobileLabel: "Deposit",
      renderCell: (row) => {
        const value = Number.parseFloat(row.deposit);
        return (
          <span
            className={
              !isNaN(value) && value > 0 ? "text-green-600" : "text-gray-950"
            }
          >
            {row.deposit}
          </span>
        );
      },
    },
    {
      key: "withdraw",
      header: "Withdraw",
      mobileLabel: "Withdraw",
      renderCell: (row) => {
        const value = Number.parseFloat(row.withdraw);
        return (
          <span
            className={
              !isNaN(value) && value > 0 ? "text-red-600" : "text-gray-950"
            }
          >
            {row.withdraw}
          </span>
        );
      },
    },
    { key: "balance", header: "Balance", mobileLabel: "Balance" },
    { key: "remark", header: "Remarks", mobileLabel: "Remarks" },
    { key: "fromTo", header: "From/To", mobileLabel: "From/To" },
  ];

  return (
    <div className="mx-auto bg-light-bg dark:bg-dark-bg border-t-2 rounded-t-lg border-gray-900 mx-1">
      {/* Filter Section */}
      <div className="bg-light-bg dark:bg-dark-bg p-4 space-y-4 border-2 border-gray-300">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <select
              value={dataSource}
              onChange={(e) => setDataSource(e.target.value)}
              className="w-full px-3 py-2 bg-light-bg dark:bg-dark-bg border border-gray-300 rounded focus:outline-none focus:border-blue-500 text-black dark:text-white"
            >
              <option value="">All Sources</option>
              <option value="Live Data">Live Data</option>
              <option value="Backup Data">Backup Data</option>
              <option value="Old Data">Old Data</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="relative">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 bg-light-bg dark:bg-dark-bg border border-gray-300 rounded focus:outline-none focus:border-blue-500 text-black dark:text-white pr-10"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
          <div className="relative">
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 bg-light-bg dark:bg-dark-bg border border-gray-300 rounded focus:outline-none focus:border-blue-500 text-black dark:text-white pr-10"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
        </div>
        <div>
          <button
            onClick={handleGetCommission}
            disabled={loading}
            className={`w-full bg-[#6A7D88] hover:bg-slate-700 text-white font-medium py-2 px-4 rounded transition-colors ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            Get Statement
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-100 text-red-700 border border-red-300 rounded mt-4">
          {error}
        </div>
      )}

      {/* Loader */}
      {loading && <Loader />}

      {/* Account Statement Table */}
      {!loading && !error && (
        <CommonTable
          title="Account Statement"
          data={processedTransactions}
          columns={columns}
          showEntriesOptions={[10, 25, 50, 100]}
          initialEntriesPerPage={10}
          showPagination={true}
          emptyMessage="No transactions found for the selected period."
        />
      )}
    </div>
  );
};

export default AccountStatement;
